import {
    Cross1Icon,
    MagnifyingGlassIcon,
    ClockIcon,
} from '@radix-ui/react-icons';
import { IconButton, TextField, Box, Flex, Text } from '@radix-ui/themes';
import { RootProps } from '@radix-ui/themes/components/text-field';
import * as React from 'react';

import styles from './SearchBar.module.css';
import { SearchBarSlot, SlotMode } from './SearchBarSlot';
import { mergeWithDefault } from '@common/utils/mergeWithDefault';
import { useListNavigation } from '@common/hooks/useListNavigation';
import { SuggestionDropdown } from '@common/components/SuggestionDropdown/SuggestionDropdown';

interface SlotProps {
    as?: SlotMode;
    side?: TextField.SlotProps['side'];
}

export interface SearchBarBaseProps
    extends Omit<RootProps, 'onSubmit' | 'slot' | 'value'> {
    value?: string;
    slot?: SlotProps;
    suggestions?: string[];
    onClear?: () => void;
    onSubmit?: (searchTerm: string) => void;
    onRemoveSuggestion?: (searchTerm: string) => void;
}

const DEFAULT_SLOT: SlotProps = { as: 'button', side: 'right' };

export const SearchBarBase = ({
    slot = {},
    value = '',
    suggestions = [],
    onClear,
    name = 'search',
    placeholder,
    onChange,
    onSubmit,
    onRemoveSuggestion,
    ...rest
}: SearchBarBaseProps) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [searchText, setSearchText] = React.useState(() => value);
    const [isFocused, setIsFocused] = React.useState(false);

    const { as, side } = mergeWithDefault(slot, DEFAULT_SLOT);
    const hasSlot = as !== undefined;

    const availableSuggestions = React.useMemo(
        () =>
            suggestions
                .filter((s) =>
                    s.toLowerCase().includes(searchText.toLowerCase()),
                )
                .slice(0, 5),
        [suggestions, searchText],
    );

    const showDropdown = isFocused && availableSuggestions.length > 0;

    const submitSearch = React.useCallback(
        (term: string) => {
            if (!term.trim()) return;
            onSubmit?.(term);
            setSearchText(term);
            setIsFocused(false);
            inputRef.current?.blur();
        },
        [onSubmit],
    );

    const { highlightedIndex, setHighlightedIndex, handleKeyDown } =
        useListNavigation({
            itemCount: availableSuggestions.length,
            isOpen: showDropdown,
            onSelect: (index) => submitSearch(availableSuggestions[index]),
            onClose: () => setIsFocused(false),
        });

    const handleInputChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            onChange?.(event);
            setSearchText(event.target.value);
            setHighlightedIndex(0);
        },
        [onChange, setHighlightedIndex],
    );

    const handleClear = React.useCallback(() => {
        onClear?.();
        setSearchText('');
        if (inputRef.current) inputRef.current.focus();
    }, [onClear]);

    const handleFormSubmit = React.useCallback(
        (event: React.FormEvent) => {
            event.preventDefault();
            submitSearch(searchText);
        },
        [searchText, submitSearch],
    );

    return (
        <Box style={{ position: 'relative', width: '100%' }}>
            <form onSubmit={handleFormSubmit}>
                <TextField.Root
                    className={styles.searchBar}
                    ref={inputRef}
                    name={name}
                    placeholder={placeholder}
                    value={searchText}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    autoComplete='off'
                    {...rest}
                >
                    {searchText && (
                        <TextField.Slot side='right'>
                            <IconButton
                                className={styles.clear}
                                size={'1'}
                                variant='ghost'
                                onClick={handleClear}
                                aria-label='Clear search'
                                asChild
                            >
                                <Cross1Icon />
                            </IconButton>
                        </TextField.Slot>
                    )}

                    {hasSlot && (
                        <TextField.Slot side={side}>
                            <SearchBarSlot as={as} onClick={handleFormSubmit}>
                                <MagnifyingGlassIcon className={styles.icon} />
                            </SearchBarSlot>
                        </TextField.Slot>
                    )}
                </TextField.Root>
            </form>

            {showDropdown && (
                <SuggestionDropdown<string>
                    items={availableSuggestions}
                    highlightedIndex={highlightedIndex}
                    onHighlight={setHighlightedIndex}
                    onSelect={submitSearch}
                    renderItem={(item, isHighlighted) => (
                        <Flex
                            align='center'
                            justify='between'
                            style={{ padding: 'var(--space-2) var(--space-3)' }}
                        >
                            <Flex
                                align='center'
                                gap='2'
                                style={{ overflow: 'hidden' }}
                            >
                                <ClockIcon
                                    style={{
                                        color: 'var(--gray-9)',
                                        flexShrink: 0,
                                    }}
                                />
                                <Text size='2' truncate>
                                    {item}
                                </Text>
                            </Flex>

                            {onRemoveSuggestion && isHighlighted && (
                                <IconButton
                                    size='1'
                                    variant='ghost'
                                    color='gray'
                                    aria-label='Remove search history'
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onRemoveSuggestion(item);

                                        inputRef.current?.focus();
                                    }}
                                >
                                    <Cross1Icon
                                        style={{
                                            width: '12px',
                                            height: '12px',
                                        }}
                                    />
                                </IconButton>
                            )}
                        </Flex>
                    )}
                />
            )}
        </Box>
    );
};
